<template>
    <div class="h-full w-full top-0 left-0 absolute flex items-center justify-center bg-black/20 z-1" @click.self="hideDialog">
        <div class="bg-base-100 p-4 shadow w-96">
            <div class="flex justify-between items-center mb-2">
                <div class="text-lg font-bold">Eksporter rapport </div>
                <div class="w-[172px]">
                    <SimpleSelect
                        v-model="selectedReportType"
                        :options="reportTypes"
                        placeholder=""
                        label="name"
                        track-by="value"
                    />
                </div>
            </div>
            <div class="flex justify-between items-center mb-4">
                for periode: <date-range-selector v-model="dateRange" />
            </div>
            <div class="mt-2">
                Velg format og mottakere for eksport
                <br>Alle rapporter blir også tilgjengelig i
                <targeting-router-link class="text-primary" :to="EXPORTS" @click="hideDialog">rapportarkivet.</targeting-router-link>
                <table class="w-full mt-2">
                    <thead>
                        <tr>
                            <th class="w-24"/>
                            <th>Last ned</th>
                            <th>Send epost</th>
                        </tr>
                    </thead>
                    <tr v-for="exportOption in exportOptions" :key="exportOption.type">
                        <td><i :class="exportOption.icon" class="inline-block text-lg mr-1 -mb-0.5"/>{{ exportOption.type }}</td>
                        <td class="text-center">
                            <input
                                v-model="exportOption.download"
                                class="checkbox"
                                type="checkbox"
                                :disabled="exportOption.disabled"
                            >
                        </td>
                        <td class="text-center">
                            <input
                                v-model="exportOption.email"
                                class="checkbox"
                                type="checkbox"
                                disabled
                            >
                        </td>
                    </tr>
                </table>
            </div>
            <div v-if="anyEmail" class="mt-4">
                <input
                    v-for="(email, index) in emailRecipients"
                    :key="index"
                    :value="email"
                    type="email"
                    class="input input-sm input-bordered w-full mb-2"
                    placeholder="Epostadresse for mottaker"
                    @change="event => updateEmailAtIndex(event, index)"
                >
                <button class="btn btn-sm btn-outline mb-2 flex" @click="addRecipientLine">
                    Legg til epost
                </button>
            </div>
            <div class="flex gap-2 justify-end mt-4">
                <button class="btn btn-outline w-24 btn-sm" @click="hideDialog">Avbryt</button>
                <button class="btn btn-primary btn-sm w-24" :disabled="isLoading" @click="submit">
                    <span v-if="isLoading" class="loading"/>
                    <template v-else>
                        OK
                    </template>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">

import hotkeys from 'hotkeys-js'
import useDynamicComponentLoader from '@/dynamic-component-loader/dynamicComponent.service'
import {ReportType} from '@/types/reports/Report'
import { TargetingRouterLink } from '@tritt/multipanel'
import {EXPORTS} from '@/exports/index'
import {computed, onMounted, ref} from 'vue'
import {getUser} from '@/services/infrastructure/auth.service'
import {PatronOverview} from '@tritt/patron-client-ts'
import {selectedOrg} from '@/tenants/org.service'
import {fetchPatronOverview} from '@/services/infrastructure/patron.service'
import {ORG_SWITCHED} from '@/types/events'
import emitter from '@/services/infrastructure/emitter'
import {defaultExportOptions, ExportOption, ExportQuery, exportReport} from '@/exports/exports.service'
import DateRangeSelector from '@/dateUtils/DateRangeSelector.vue'
import {format} from 'date-fns'
import {useI18n} from 'vue-i18n'
import SimpleSelect from '@/components/SimpleSelect.vue'

const {t} = useI18n()

const {clearDynamicComponent} = useDynamicComponentLoader()

const props = defineProps({
    reportType: {
        type: String as () => ReportType,
        required: true
    },
    exportQuery: {
        type: Object as () => ExportQuery,
        required: true
    }
})

const query = ref<ExportQuery>(props.exportQuery)

const dateRange = computed({
    get: () => {
        return {
            start: new Date(query.value.fromDate),
            end: new Date(query.value.toDate)
        }
    },
    set: (dateRange) => {
        query.value.fromDate = format(dateRange.start, 'yyyy-MM-dd')
        query.value.toDate = format(dateRange.end, 'yyyy-MM-dd')
    }
})

const reportTypes = computed(() => {
    return Object.values(ReportType).map(type => ({
        value: type,
        name: t(`reportType.${type}`)
    }))
})

const selected = ref<ReportType>(props.reportType)

const selectedReportType = computed({
    get: () => reportTypes.value.find(option => option.value === selected.value),
    set: (value: { value: ReportType }) => {
        selected.value = value.value
    }
})

hotkeys('esc', () => hideDialog())

const hideDialog = clearDynamicComponent

const exportOptions = ref<ExportOption[]>(defaultExportOptions)

const anyEmail = computed(() => exportOptions.value.some(option => option.email))

const emailRecipients = ref<string[]>([])

const addRecipientLine = () => {
    emailRecipients.value.push('')
}

const updateEmailAtIndex = (event: Event, index: number) => {
    const input = event.target as HTMLInputElement
    if (input.value) {
        emailRecipients.value[index] = input.value
    } else {
        emailRecipients.value.splice(index, 1)
    }
}

const patronOverview = ref<PatronOverview>()
const refreshPatronData = async () => {
    if (selectedOrg.value) {
        patronOverview.value = await fetchPatronOverview(selectedOrg.value.organizationId)
    }
}

onMounted(() => {
    const currentUserEmail = getUser.value?.email
    if (currentUserEmail) {
        emailRecipients.value.push(currentUserEmail)
    }

    refreshPatronData().then(() => {
            const companyEmail = patronOverview.value?.companyEmail
            if (companyEmail && !emailRecipients.value.includes(companyEmail)) {
                emailRecipients.value.push(companyEmail)
            }
        }
    )
    emitter.on(ORG_SWITCHED, hideDialog)
})

const isLoading = ref(false)

const submit = async () => {
    const reportType = selectedReportType.value?.value || props.reportType

    isLoading.value = true
    await exportReport(reportType, query.value, exportOptions.value)
    isLoading.value = false
    hideDialog()
}

</script>

<style scoped>

</style>